import omit from 'lodash/omit'
import uuid from '@vend/vend-uuid'
import { ApiLineItemTaxComponent, ApiTax } from '@vend/types'
import { FulfilmentType } from './fulfilment'

/**
 * JSON representation of a discount on a sale that is persisted to IndexedDB.
 */
export interface SaleDiscountJSON {
  localId: string
  id: string | null
  price: string | number
  tax: string | number
  tax_id: string | null
  taxComponents: object[] | null
  registerId: string
  registerSequenceId: string
  fulfilmentType: FulfilmentType | undefined
}

/**
 * Represents a discount applied to a sale.
 */
export interface SaleDiscount {
  /**
   * The unique ID of this SaleDiscount locally generated on the client.
   *
   * @type UUID
   */
  localId: string
  id: string | null
  price: string | number
  tax: string | number
  taxInfo: ApiTax | null
  registerId: string
  registerSequenceId: string
  fulfilmentType: FulfilmentType | undefined
  /**
   * Line item tax components are the total tax amounts grouped by rate id.
   *
   * This data comes from the 2.0 sales API and is the most correct version of tax data.
   * Currently used for the tax summary in sales history.
   */
  taxComponents: ApiLineItemTaxComponent[] | null
}

export const createSaleDiscount = (
  data: Partial<SaleDiscount> = {}
): SaleDiscount => {
  const localId = data.localId || uuid()
  const id = data.id || null
  const price = data.price || '0'
  const tax = data.tax || '0'
  const taxInfo = data.taxInfo || null
  const taxComponents = data.taxComponents || null
  const registerId = data.registerId!
  const registerSequenceId = data.registerSequenceId!
  const fulfilmentType = data.fulfilmentType || undefined
  return {
    id,
    localId,
    price,
    tax,
    taxInfo,
    taxComponents,
    registerId,
    registerSequenceId,
    fulfilmentType,
  }
}

export const saleDiscountToJSON = (
  saleDiscount: SaleDiscount
): SaleDiscountJSON => {
  return {
    ...omit<typeof saleDiscount, 'taxInfo'>(saleDiscount, 'taxInfo'),
    tax_id: saleDiscount.taxInfo && saleDiscount.taxInfo.id,
  }
}
